/**
 * `value-box` (`LN-WGT03`, the shape ruled in the `LN-JS-M1` review): a
 * program run one line at a time beside its variables as boxes, a `let` box
 * taking a new value and a `const` box refusing one.
 *
 * <div class="pc-widget" data-widget="value-box" data-code="let price = 120;
 * const rate = 5;
 * rate = 6;"
 *      data-steps="[{&quot;line&quot;:1,&quot;boxes&quot;:[{&quot;name&quot;:&quot;price&quot;,&quot;kind&quot;:&quot;let&quot;,&quot;value&quot;:&quot;120&quot;}],&quot;changed&quot;:&quot;price&quot;,&quot;note&quot;:&quot;A box called price.&quot;}]"
 *      data-caption="let boxes take new values; a const box refuses.">
 *   <p class="pc-widget-fallback">price holds 120; rate holds 5 and refuses 6.</p>
 * </div>
 *
 * `data-code` (required): the program, one line per newline. `data-steps`
 * (required): a JSON list of `{ line, boxes, changed?, refused?, error?, note }`:
 * `line` the 1-based line that has just run (highlighted); `boxes` the whole
 * picture after it, in declaration order, each `{ name, kind, value, type? }`
 * with `kind` `"let"` or `"const"`, `value` JavaScript source text and `type`
 * the `typeof` drawn under the box; `changed` the box (or boxes) just written;
 * `refused` the `const` box that turned the write away and `error` the
 * engine's message, on the last step; `note` one sentence in the lesson's
 * language. `data-caption` (optional). The first frame is step 1. M8 extends
 * this with optional fields only.
 */

import {
  attachStepper,
  buildShell,
  el,
  isIndex,
  isNonEmptyString,
  isRecord,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

interface ValueBox {
  name: string;
  kind: "let" | "const";
  value: string;
  type?: string;
}

interface ValueStep {
  line: number;
  boxes: ValueBox[];
  changed?: string | string[];
  refused?: string;
  error?: string;
  note: string;
}

const isBox = (v: unknown): v is ValueBox =>
  isRecord(v) &&
  isNonEmptyString(v.name) &&
  (v.kind === "let" || v.kind === "const") &&
  typeof v.value === "string" &&
  (v.type === undefined || typeof v.type === "string");

const optionalString = (v: unknown) => v === undefined || typeof v === "string";

const isSteps =
  (lines: number) =>
  (value: unknown): value is ValueStep[] =>
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (step) =>
        isRecord(step) &&
        isIndex(step.line) &&
        step.line >= 1 &&
        step.line <= lines &&
        Array.isArray(step.boxes) &&
        step.boxes.every(isBox) &&
        (optionalString(step.changed) ||
          (Array.isArray(step.changed) && step.changed.every((name) => typeof name === "string"))) &&
        optionalString(step.refused) &&
        optionalString(step.error) &&
        typeof step.note === "string",
    );

export function renderValueBox(host: HTMLElement, text: WidgetText): boolean {
  const code = host.dataset.code;
  if (!code) return false;
  const lines = code.split(/\r?\n/);
  const steps = parseJson(host.dataset.steps, isSteps(lines.length));
  if (!steps) return false;

  const shell = buildShell(host, {
    label: text("labelValueBox"),
    caption: host.dataset.caption,
  });

  const program = el("ol", "pc-value-code");
  const rows = lines.map((line) => {
    const row = el("li", undefined, line);
    program.append(row);
    return row;
  });
  const boxes = el("div", "pc-widget-cells");
  const error = el("p", "pc-value-error");
  const note = liveNote();
  shell.body.append(program, boxes, error, note);

  attachStepper(
    shell,
    steps.length,
    (index) => {
      const step = steps[index];
      rows.forEach((row, i) => {
        row.classList.toggle("is-active", i === step.line - 1);
        if (i === step.line - 1) row.setAttribute("aria-current", "step");
        else row.removeAttribute("aria-current");
      });

      const changed = Array.isArray(step.changed) ? step.changed : step.changed ? [step.changed] : [];
      boxes.textContent = "";
      for (const box of step.boxes) {
        const cell = el("div", "pc-cell pc-value-box");
        if (box.kind === "const") cell.classList.add("is-const");
        if (changed.includes(box.name)) cell.classList.add("is-active");
        if (box.name === step.refused) cell.classList.add("is-refused");
        const name = el("span", "pc-cell-name");
        name.append(el("span", "pc-value-kind", box.kind), ` ${box.name}`);
        cell.append(name, el("span", "pc-cell-value", box.value));
        if (box.type !== undefined) cell.append(el("span", "pc-cell-meta", box.type));
        if (box.name === step.refused) cell.append(el("span", "pc-value-refused", text("valueRefused")));
        boxes.append(cell);
      }

      error.textContent = step.error ?? "";
      error.hidden = !step.error;
      note.textContent = step.note;
    },
    text,
    { liveReadout: false, stepLabel: (index) => text("valueLine", { n: steps[index].line }) },
  );
  return true;
}
